var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { createPaymentOrder, updatePaymentOrder, } from "../../redux/payment/payment.actions";
import * as paymentOrderActions from "../../reduxV2/paymentOrder/paymentOrder.actions";
import { EPaymentFeatures, EPaymentOrderType, } from "../../types/payment.types";
import { getAdditionalCostPaymentOrder, getAdditionalCostPaymentOrderV2, getCommissionPaymentOrder, getCommissionPaymentOrderV2, getCommonCostsPaymentOrder, getCommonCostsPaymentOrderV2, getManagementFeePaymentOrder, getManagementFeePaymentOrderV2, getNonCommonCostsPaymentOrder, getNonCommonCostsPaymentOrderV2, getPaymentGroup, getPaymentOrder, getPaymentOrderV2, } from "./schema.createPayment.actions.utils";
import { EField, } from "./schema.createPayment.types";
import { getRelatedContract } from "./schema.createPayment.utils";
var PENALTY_ELIGIBLE_TYPES = new Set([
    EPaymentOrderType.Rent,
    EPaymentOrderType.CommonCosts,
    EPaymentOrderType.NonCommonCosts,
    EPaymentOrderType.AdditionalCosts,
]);
var applyLatePaymentPenaltyFeature = function (_a) {
    var paymentOrders = _a.paymentOrders, formData = _a.formData, extraData = _a.extraData, _b = _a.existingFeatures, existingFeatures = _b === void 0 ? [] : _b;
    // Without the office feature `features` stays out of the payload, so the update PATCH
    // leaves an existing order's features as they are.
    if (!extraData.latePaymentPenaltyFeatureEnabled) {
        return;
    }
    paymentOrders.forEach(function (paymentOrder, index) {
        if (!PENALTY_ELIGIBLE_TYPES.has(paymentOrder.type)) {
            return;
        }
        var features = index === 0 ? existingFeatures : paymentOrder.features || [];
        paymentOrder.features = formData[EField.ApplyPenalty]
            ? Array.from(new Set(__spreadArray(__spreadArray([], features, true), [EPaymentFeatures.LatePaymentPenalty], false)))
            : features.filter(function (feature) { return feature !== EPaymentFeatures.LatePaymentPenalty; });
    });
};
export var isPerformingSubmitActionSelector = function (paymentOrder, useV2Payments) {
    return function (state) {
        var _a, _b;
        if (paymentOrder) {
            var updateStateUpdating = useV2Payments
                ? (_a = state.PAYMENT_ORDERS_V2.update[paymentOrder.id]) === null || _a === void 0 ? void 0 : _a.pending
                : (_b = state.payment.updatePaymentOrders[paymentOrder.id]) === null || _b === void 0 ? void 0 : _b.isUpdatingPaymentOrder;
            if (updateStateUpdating) {
                return updateStateUpdating;
            }
            return false;
        }
        return useV2Payments
            ? state.PAYMENT_ORDERS_V2.createPaymentOrders.pending || false
            : !!state.payment.isCreatingPaymentOrder;
    };
};
export var performingSubmitActionErrorSelector = function (paymentOrder, useV2Payments) {
    return function (state) {
        var _a, _b;
        if (paymentOrder) {
            var updateStateError = useV2Payments
                ? (_a = state.PAYMENT_ORDERS_V2.update[paymentOrder.id]) === null || _a === void 0 ? void 0 : _a.error
                : (_b = state.payment.updatePaymentOrders[paymentOrder.id]) === null || _b === void 0 ? void 0 : _b.fetchPaymentOrdersError;
            if (updateStateError) {
                return updateStateError;
            }
            return;
        }
        return useV2Payments
            ? state.PAYMENT_ORDERS_V2.createPaymentOrders.error
            : state.payment.createPaymentOrderError;
    };
};
export var submitActionCreator = function (extraData, propertyId, paymentOrderId, paymentOrder, useV2Payments) {
    return function (formData) {
        var _a, _b, _c, _d, _e;
        var contract = getRelatedContract(extraData, formData);
        var contractId = contract === null || contract === void 0 ? void 0 : contract.id;
        if (paymentOrderId) {
            if (useV2Payments) {
                var updatedPaymentOrderV2 = getPaymentOrderV2(formData, extraData);
                applyLatePaymentPenaltyFeature({
                    paymentOrders: [updatedPaymentOrderV2],
                    formData: formData,
                    extraData: extraData,
                    existingFeatures: paymentOrder === null || paymentOrder === void 0 ? void 0 : paymentOrder.features,
                });
                var paymentGroupDto = getPaymentGroup(formData, propertyId, extraData);
                return paymentOrderActions.updateStart.getAction({
                    id: paymentOrderId,
                    data: __assign(__assign({}, updatedPaymentOrderV2), { paymentGroupDto: paymentGroupDto }),
                });
            }
            var updatedPaymentOrder = getPaymentOrder(formData, propertyId, extraData);
            applyLatePaymentPenaltyFeature({
                paymentOrders: [updatedPaymentOrder],
                formData: formData,
                extraData: extraData,
                existingFeatures: paymentOrder === null || paymentOrder === void 0 ? void 0 : paymentOrder.features,
            });
            return updatePaymentOrder.actions.start({
                paymentOrderId: paymentOrderId,
                paymentOrder: updatedPaymentOrder,
            });
        }
        if (useV2Payments) {
            var mainPaymentOrder_1 = getPaymentOrderV2(formData, extraData);
            // Cost-settlement flow split over several tenants: one submission, but one
            // payment order + its OWN payment group per tenant → each tenant gets a
            // separate open payment (a single group would bundle them onto one payer).
            var costSplits = ((_a = extraData.prefill) === null || _a === void 0 ? void 0 : _a.costPayment)
                ? extraData.prefill.costSplits
                : undefined;
            if (costSplits && costSplits.length > 1) {
                var sharedGroup_1 = getPaymentGroup(formData, propertyId, extraData);
                return paymentOrderActions.createPaymentOrdersStart.getAction({
                    data: {
                        paymentOrders: [],
                        bulkGroups: costSplits.map(function (split) {
                            var _a, _b, _c;
                            return ({
                                paymentOrders: [
                                    __assign(__assign(__assign(__assign({}, mainPaymentOrder_1), { payerId: (_a = split.payerId) !== null && _a !== void 0 ? _a : mainPaymentOrder_1.payerId, amount: split.amount }), (split.documentId ? { documentId: split.documentId } : {})), (split.description
                                        ? { otherDescription: split.description }
                                        : {})),
                                ],
                                // A payment group belongs to ONE property. When an entry names
                                // its own property (a settlement over a group bills each tenant
                                // on their own unit), that entry's group is created there instead
                                // of on the form's property — otherwise every tenant's order and
                                // open payment would land on the same property. The same for
                                // its lease: each tenant's payment is linked to their own.
                                paymentGroup: __assign(__assign({}, sharedGroup_1), { propertyId: (_b = split.propertyId) !== null && _b !== void 0 ? _b : sharedGroup_1.propertyId, contractId: (_c = split.contractId) !== null && _c !== void 0 ? _c : sharedGroup_1.contractId }),
                            });
                        }),
                    },
                    // The same attachment fields the single-group branch passes: a cost
                    // payment split over several tenants carries the invoice too, and the
                    // saga puts it on every entry (an entry with its own documentId keeps it).
                    document: formData[EField.Document] ||
                        (((_b = extraData.prefill) === null || _b === void 0 ? void 0 : _b.documentId)
                            ? [extraData.prefill.documentId]
                            : undefined),
                    documentCategory: formData[EField.DocumentCategory] || undefined,
                    contractId: formData[EField.LinkToContract] !== false
                        ? contract === null || contract === void 0 ? void 0 : contract.id
                        : undefined,
                });
            }
            var paymentOrders_1 = [];
            paymentOrders_1.push(mainPaymentOrder_1);
            var commonCostsPaymentOrder_1 = getCommonCostsPaymentOrderV2(formData, contract);
            if (commonCostsPaymentOrder_1) {
                paymentOrders_1.push(commonCostsPaymentOrder_1);
            }
            var nonCommonCostsPaymentOrder_1 = getNonCommonCostsPaymentOrderV2(formData, contract);
            if (nonCommonCostsPaymentOrder_1) {
                paymentOrders_1.push(nonCommonCostsPaymentOrder_1);
            }
            var commissionPaymentOrder_1 = getCommissionPaymentOrderV2(formData, contract);
            if (commissionPaymentOrder_1) {
                paymentOrders_1.push(commissionPaymentOrder_1);
            }
            var managementFeePaymentOrder_1 = getManagementFeePaymentOrderV2(formData, contract);
            if (managementFeePaymentOrder_1) {
                paymentOrders_1.push(managementFeePaymentOrder_1);
            }
            if (((_c = formData === null || formData === void 0 ? void 0 : formData.additionalCostsArray) === null || _c === void 0 ? void 0 : _c.length) > 0) {
                formData === null || formData === void 0 ? void 0 : formData.additionalCostsArray.forEach(function (cost) {
                    var bookkeepingSettings = formData.additionalCostBookkeepingArray.find(function (settings) {
                        return settings.id === cost.id;
                    });
                    var additionalCostPaymentOrder = getAdditionalCostPaymentOrderV2({
                        formData: formData,
                        costInfo: cost,
                        bookkeepingInfo: bookkeepingSettings,
                        contract: contract,
                        extraData: extraData,
                    });
                    paymentOrders_1.push(additionalCostPaymentOrder);
                });
            }
            applyLatePaymentPenaltyFeature({ paymentOrders: paymentOrders_1, formData: formData, extraData: extraData });
            var paymentGroup = getPaymentGroup(formData, propertyId, extraData);
            return paymentOrderActions.createPaymentOrdersStart.getAction({
                data: { paymentOrders: paymentOrders_1, paymentGroup: paymentGroup },
                // An attachment the form was opened with (an existing document id)
                // when the user did not add one themselves.
                document: formData[EField.Document] ||
                    (((_d = extraData.prefill) === null || _d === void 0 ? void 0 : _d.documentId)
                        ? [extraData.prefill.documentId]
                        : undefined),
                // Only an explicit "off" unlinks: the switch defaults to on (step 2a)
                contractId: formData[EField.LinkToContract] !== false ? contract === null || contract === void 0 ? void 0 : contract.id : undefined,
                documentCategory: formData[EField.DocumentCategory] || undefined,
            });
        }
        var paymentOrders = [];
        var mainPaymentOrder = getPaymentOrder(formData, propertyId, extraData);
        paymentOrders.push(mainPaymentOrder);
        var commonCostsPaymentOrder = getCommonCostsPaymentOrder(formData, propertyId, contractId, contract);
        if (commonCostsPaymentOrder) {
            paymentOrders.push(commonCostsPaymentOrder);
        }
        var nonCommonCostsPaymentOrder = getNonCommonCostsPaymentOrder(formData, propertyId, contractId, contract);
        if (nonCommonCostsPaymentOrder) {
            paymentOrders.push(nonCommonCostsPaymentOrder);
        }
        var commissionPaymentOrder = getCommissionPaymentOrder(formData, propertyId, contractId, contract);
        if (commissionPaymentOrder) {
            paymentOrders.push(commissionPaymentOrder);
        }
        var managementFeePaymentOrder = getManagementFeePaymentOrder(formData, propertyId, contractId, contract);
        if (managementFeePaymentOrder) {
            paymentOrders.push(managementFeePaymentOrder);
        }
        if (((_e = formData === null || formData === void 0 ? void 0 : formData.additionalCostsArray) === null || _e === void 0 ? void 0 : _e.length) > 0) {
            formData === null || formData === void 0 ? void 0 : formData.additionalCostsArray.forEach(function (cost) {
                var bookkeepingSettings = formData.additionalCostBookkeepingArray.find(function (settings) {
                    return settings.id === cost.id;
                });
                var additionalCostPaymentOrder = getAdditionalCostPaymentOrder({
                    formData: formData,
                    extraData: extraData,
                    costInfo: cost,
                    bookkeepingInfo: bookkeepingSettings,
                    propertyId: propertyId,
                    contract: contract,
                });
                paymentOrders.push(additionalCostPaymentOrder);
            });
        }
        applyLatePaymentPenaltyFeature({ paymentOrders: paymentOrders, formData: formData, extraData: extraData });
        return createPaymentOrder.actions.start({
            paymentOrders: paymentOrders,
            document: formData[EField.Document] || undefined,
            // Only an explicit "off" unlinks: the switch defaults to on (step 2a)
            contractId: formData[EField.LinkToContract] !== false ? contract === null || contract === void 0 ? void 0 : contract.id : undefined,
            propertyId: propertyId,
            documentCategory: formData[EField.DocumentCategory] || undefined,
        });
    };
};
